/**
 * La bitácora de la casa — todo lo que ha ocurrido ahí, y a qué venta es achacable.
 *
 * Dos entidades, ambas colgadas de la casa (hoja de ruta, punto 6):
 *
 *   · INTERVENCIÓN — instalación, mantenimiento, diagnóstico, visita de garantía o
 *     reparación cobrada. Con técnico, fecha, fotos, y referencias a la cotización
 *     que la origina y al folio de factura (a mano, hasta que exista el puente a
 *     Books). Es el registro de campo: quién fue, qué hizo, qué encontró.
 *
 *   · GARANTÍA como objeto propio, no como palomita. Nace de un trabajo (la
 *     instalación original o una reparación cobrada), con ORIGEN (qué cotización/
 *     factura la ampara), ALCANCE (el texto que evita discusiones) y VIGENCIA.
 *     Una visita de garantía referencia la garantía que la ampara → la cadena
 *     factura → garantía → visita queda trazable en ambos sentidos.
 *
 * Frontera con Zoho Desk (mapa de funciones): el reporte del cliente ENTRA por
 * Desk; si requiere visita, la intervención nace aquí con referencia cruzada. El
 * cierre técnico vive aquí; el de atención al cliente, en Desk.
 */
import { useEffect, useState } from 'react'
import * as store from '../datos/store.js'
import { usuario } from '../firebase.js'
import { BarraSimple, CapturaFotos, FotosRevision } from './Encargo.jsx'
import VistaDictamen from './Dictamen.jsx'

export const TIPOS_INTERVENCION = {
  instalacion: 'Instalación',
  mantenimiento: 'Mantenimiento',
  diagnostico: 'Diagnóstico',
  garantia: 'Visita de garantía',
  reparacion: 'Reparación cobrada'
}

const fechaCorta = iso => (iso || '').slice(0, 10)
const hoyIso = () => new Date().toISOString().slice(0, 10)
export const garantiaVigente = g => (g.fin || '') >= hoyIso()

/* ---------- Formulario de garantía ---------- */

function FormGarantia({ casa, cotizaciones, onCerrar }) {
  const [origen, setOrigen] = useState(cotizaciones[0]?.id || '')
  const [facturaRef, setFacturaRef] = useState('')
  const [alcance, setAlcance] = useState('')
  const [fin, setFin] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const lista = !alcance.trim() || !fin

  const guardar = async () => {
    if (lista) return
    setOcupado(true)
    try {
      await store.guardar('garantias', {
        casa: casa.id, cliente: casa.cliente,
        origenCotizacionId: origen || '', facturaRef: facturaRef.trim(),
        alcance: alcance.trim(), inicio: hoyIso(), fin
      })
      onCerrar()
    } finally { setOcupado(false) }
  }

  return (
    <div className="card">
      <label className="f">
        <span>Trabajo que la origina</span>
        <select value={origen} onChange={e => setOrigen(e.target.value)}>
          {cotizaciones.map(c => <option key={c.id} value={c.id}>{c.nombre} · {c.fecha}</option>)}
          <option value="">Sin cotización en el sistema</option>
        </select>
      </label>
      <label className="f">
        <span>Folio de factura (de Books, a mano por ahora)</span>
        <input value={facturaRef} onChange={e => setFacturaRef(e.target.value)} />
      </label>
      <label className="f">
        <span>Alcance — qué cubre EXACTAMENTE (este texto evita discusiones)</span>
        <textarea value={alcance} style={{ minHeight: 70 }}
          placeholder="P. ej.: mano de obra y reposición del apagador instalado; no cubre daños por sobretensión de la red…"
          onChange={e => setAlcance(e.target.value)} />
      </label>
      <label className="f">
        <span>Vigente hasta</span>
        <input type="date" value={fin} onChange={e => setFin(e.target.value)} />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="ib" onClick={onCerrar}>Cancelar</button>
        <button className="ib pri" disabled={lista || ocupado} onClick={guardar}>
          {ocupado ? 'Guardando…' : 'Registrar garantía'}
        </button>
      </div>
    </div>
  )
}

/* ---------- Formulario de intervención ---------- */

function FormIntervencion({ casa, cotizaciones, garantias, onCerrar }) {
  const [tipo, setTipo] = useState('mantenimiento')
  const [descripcion, setDescripcion] = useState('')
  const [fotos, setFotos] = useState([])
  const [cotizacionId, setCotizacionId] = useState('')
  const [facturaRef, setFacturaRef] = useState('')
  const [garantiaId, setGarantiaId] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const vigentes = garantias.filter(garantiaVigente)

  const guardar = async () => {
    if (!descripcion.trim()) return
    setOcupado(true)
    try {
      await store.guardar('intervenciones', {
        casa: casa.id, cliente: casa.cliente, tipo,
        fecha: new Date().toISOString(),
        tecnico: usuario()?.email || 'Soham (modo local)',
        descripcion: descripcion.trim(),
        fotos,
        cotizacionId, facturaRef: facturaRef.trim(),
        garantiaId: tipo === 'garantia' ? garantiaId : ''
      })
      onCerrar()
    } finally { setOcupado(false) }
  }

  return (
    <div className="card">
      <label className="f">
        <span>Tipo de intervención</span>
        <select value={tipo} onChange={e => setTipo(e.target.value)}>
          {Object.entries(TIPOS_INTERVENCION).map(([k, n]) => <option key={k} value={k}>{n}</option>)}
        </select>
      </label>
      {tipo === 'garantia' && (
        vigentes.length ? (
          <label className="f">
            <span>Garantía que ampara la visita</span>
            <select value={garantiaId} onChange={e => setGarantiaId(e.target.value)}>
              <option value="">— elegir —</option>
              {vigentes.map(g => <option key={g.id} value={g.id}>{g.alcance.slice(0, 60)} · hasta {g.fin}</option>)}
            </select>
          </label>
        ) : (
          <div className="warn">
            Esta casa no tiene garantías vigentes registradas. Si el trabajo original sí
            tenía garantía, regístrala primero; si lo que se encontró NO está cubierto,
            el tipo correcto es <b>reparación cobrada</b> — y esa emite su PROPIA garantía.
          </div>
        )
      )}
      <label className="f">
        <span>Qué se hizo y qué se encontró</span>
        <textarea value={descripcion} style={{ minHeight: 90 }}
          placeholder="El trabajo, las mediciones, la causa raíz — lo que el siguiente técnico necesita saber."
          onChange={e => setDescripcion(e.target.value)} />
      </label>
      <div className="two">
        <label className="f">
          <span>Cotización achacable (si aplica)</span>
          <select value={cotizacionId} onChange={e => setCotizacionId(e.target.value)}>
            <option value="">Ninguna</option>
            {cotizaciones.map(c => <option key={c.id} value={c.id}>{c.nombre} · {c.fecha}</option>)}
          </select>
        </label>
        <label className="f">
          <span>Folio de factura (si aplica)</span>
          <input value={facturaRef} onChange={e => setFacturaRef(e.target.value)} />
        </label>
      </div>
      <CapturaFotos fotos={fotos} onCambiar={setFotos} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="ib" onClick={onCerrar}>Cancelar</button>
        <button className="ib pri" disabled={!descripcion.trim() || ocupado} onClick={guardar}>
          {ocupado ? 'Guardando…' : 'Registrar en la bitácora'}
        </button>
      </div>
    </div>
  )
}

/* ---------- El runner de un protocolo guiado ----------
 * La app como método (hoja de ruta, punto 7): guía a quien está parado en la
 * casa — qué preguntar al llegar, qué medir con qué, y qué decir al cerrar. Al
 * sellar, TODO queda como una intervención de la bitácora: las respuestas
 * prueban que el método se siguió — defensa de garantía y control de calidad
 * del técnico al mismo tiempo. */

function RunnerProtocolo({ protocolo, casa, cotizaciones, herramientas, onCerrar }) {
  const [respuestas, setRespuestas] = useState({})   // pasoId -> { valor, ok, nota }
  const [hallazgos, setHallazgos] = useState('')
  const [fotos, setFotos] = useState([])
  const [cotizacionId, setCotizacionId] = useState('')
  const [ocupado, setOcupado] = useState(false)

  const resp = id => respuestas[id] || {}
  const responder = (id, campo, v) =>
    setRespuestas(prev => ({ ...prev, [id]: { ...prev[id], [campo]: v } }))

  /* Sellar exige haber contestado TODOS los pasos: un protocolo a medias no es
     un protocolo — es la discrecionalidad que este sistema vino a quitar. */
  const contestado = p => (p.tipo === 'medicion' ? !!resp(p.id).valor?.trim() : resp(p.id).ok !== undefined)
  const completo = protocolo.pasos.every(contestado) && hallazgos.trim()

  const sellar = async () => {
    if (!completo) return
    setOcupado(true)
    try {
      await store.guardar('intervenciones', {
        casa: casa.id, cliente: casa.cliente, tipo: 'diagnostico',
        fecha: new Date().toISOString(),
        tecnico: usuario()?.email || 'Soham (modo local)',
        descripcion: hallazgos.trim(),
        protocoloId: protocolo.id, protocoloNombre: protocolo.nombre,
        respuestas: protocolo.pasos.map(p => ({
          paso: p.titulo, tipo: p.tipo, unidad: p.unidad || '',
          valor: resp(p.id).valor || '', ok: resp(p.id).ok ?? null, nota: resp(p.id).nota || ''
        })),
        fotos, cotizacionId, facturaRef: '', garantiaId: ''
      })
      onCerrar()
    } finally { setOcupado(false) }
  }

  const nombresHerr = (protocolo.herr || [])
    .map(id => herramientas.find(h => h.id === id)?.nombre)
    .filter(Boolean)

  return (
    <div className="card">
      <div className="t">{protocolo.nombre}</div>
      {protocolo.cuando && <div className="s" style={{ marginTop: 4 }}>{protocolo.cuando}</div>}

      {nombresHerr.length > 0 && (
        <div className="s" style={{ marginTop: 8 }}>
          <b>Herramientas que exige:</b> {nombresHerr.join(' · ')}
        </div>
      )}

      {protocolo.guionLlegada?.length > 0 && (
        <>
          <div className="sec">Al llegar — pregúntale al cliente</div>
          {protocolo.guionLlegada.map((q, i) => (
            <div className="s" key={i} style={{ marginBottom: 4 }}>• {q}</div>
          ))}
        </>
      )}

      <div className="sec">Los pasos</div>
      {protocolo.pasos.map((p, i) => (
        <div className="card" key={p.id || i}>
          <div className="grow">
            <div className="t">{i + 1}. {p.titulo}</div>
            <div className="s" style={{ marginTop: 4 }}>{p.instruccion}</div>
            {p.criterio && <div className="s" style={{ marginTop: 4 }}><b>Criterio:</b> {p.criterio}</div>}
            {p.tipo === 'medicion' ? (
              <label className="f" style={{ marginTop: 8 }}>
                <span>Medición{p.unidad ? ` (${p.unidad})` : ''}</span>
                <input value={resp(p.id).valor || ''}
                  onChange={e => responder(p.id, 'valor', e.target.value)} />
              </label>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className={`ib${resp(p.id).ok === true ? ' pri' : ''}`}
                  onClick={() => responder(p.id, 'ok', true)}>Cumple</button>
                <button className={`ib${resp(p.id).ok === false ? ' pri' : ''}`}
                  onClick={() => responder(p.id, 'ok', false)}>No cumple</button>
              </div>
            )}
            <label className="f" style={{ marginTop: 8, marginBottom: 0 }}>
              <span>Nota del paso (qué se encontró)</span>
              <input value={resp(p.id).nota || ''}
                onChange={e => responder(p.id, 'nota', e.target.value)} />
            </label>
          </div>
        </div>
      ))}

      {protocolo.guionCierre?.length > 0 && (
        <>
          <div className="sec">Al cerrar — díselo al cliente</div>
          {protocolo.guionCierre.map((q, i) => (
            <div className="s" key={i} style={{ marginBottom: 4 }}>• {q}</div>
          ))}
        </>
      )}

      <label className="f" style={{ marginTop: 10 }}>
        <span>Hallazgos y dictamen — lo que queda en la bitácora</span>
        <textarea value={hallazgos} style={{ minHeight: 90 }}
          placeholder="Qué se encontró, la causa raíz, y qué se propuso con precio cerrado."
          onChange={e => setHallazgos(e.target.value)} />
      </label>
      <label className="f">
        <span>Cotización achacable (si aplica)</span>
        <select value={cotizacionId} onChange={e => setCotizacionId(e.target.value)}>
          <option value="">Ninguna</option>
          {cotizaciones.map(c => <option key={c.id} value={c.id}>{c.nombre} · {c.fecha}</option>)}
        </select>
      </label>
      <CapturaFotos fotos={fotos} onCambiar={setFotos} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="ib" onClick={onCerrar}>Cancelar</button>
        <button className="ib pri" disabled={!completo || ocupado} onClick={sellar}>
          {ocupado ? 'Sellando…' : completo ? 'Sellar en la bitácora' : 'Faltan pasos por contestar'}
        </button>
      </div>
    </div>
  )
}

/* ---------- La vista completa ---------- */

export default function VistaBitacora({ casa, cliente, cotizaciones, protocolos = [], herramientas = [], atras }) {
  const [intervenciones, setIntervenciones] = useState(null)
  const [garantias, setGarantias] = useState(null)
  const [forma, setForma] = useState(null)        // 'intervencion' | 'garantia' | null
  const [corriendo, setCorriendo] = useState(null) // protocolo en ejecución, o null
  const [dictamen, setDictamen] = useState(null)   // intervención a imprimir, o null

  useEffect(() => store.suscribirConsulta('intervenciones', 'casa', casa.id, lista =>
    setIntervenciones([...lista].sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')))), [casa.id])
  useEffect(() => store.suscribirConsulta('garantias', 'casa', casa.id, lista =>
    setGarantias([...lista].sort((a, b) => (b.fin || '').localeCompare(a.fin || '')))), [casa.id])

  const nombreCot = id => cotizaciones.find(c => c.id === id)?.nombre || null
  const garantiaDe = id => garantias?.find(g => g.id === id) || null

  /* El dictamen sustituye a la vista completa mientras se imprime: es un papel,
     no un panel — y la impresión del navegador toma la página entera. */
  if (dictamen) return (
    <VistaDictamen intervencion={dictamen} casa={casa} cliente={cliente}
      cotizaciones={cotizaciones} garantias={garantias || []}
      atras={() => setDictamen(null)} />
  )

  return (
    <div className="app">
      <BarraSimple titulo="Bitácora de la casa" migaja={`${casa.alias} · ${cliente?.nombre || ''}`}
        atras={atras} />
      <main>
        <div className="sec">Garantías</div>
        {garantias === null && <div className="empty">Cargando…</div>}
        {garantias?.length === 0 && forma !== 'garantia' && (
          <div className="empty">
            <b>Sin garantías registradas.</b>
            <p>Cada trabajo entregado debería dejar la suya: origen, alcance por escrito
               y fecha de fin. Es lo que vuelve defendible una visita posterior.</p>
          </div>
        )}
        {garantias?.map(g => (
          <div className="card" key={g.id} style={{ opacity: garantiaVigente(g) ? 1 : 0.6 }}>
            <div className="grow">
              <div className="t">{garantiaVigente(g) ? 'Vigente' : 'Vencida'} hasta {g.fin}</div>
              <div className="s">
                {nombreCot(g.origenCotizacionId) || 'Sin cotización'}
                {g.facturaRef ? ` · factura ${g.facturaRef}` : ''}
              </div>
              <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{g.alcance}</div>
            </div>
          </div>
        ))}
        {forma === 'garantia'
          ? <FormGarantia casa={casa} cotizaciones={cotizaciones} onCerrar={() => setForma(null)} />
          : <button className="discreto" onClick={() => setForma('garantia')}>Registrar una garantía</button>}

        <div className="sec">Intervenciones</div>
        {corriendo ? (
          <RunnerProtocolo protocolo={corriendo} casa={casa} cotizaciones={cotizaciones}
            herramientas={herramientas} onCerrar={() => setCorriendo(null)} />
        ) : forma === 'intervencion' ? (
          <FormIntervencion casa={casa} cotizaciones={cotizaciones} garantias={garantias || []}
            onCerrar={() => setForma(null)} />
        ) : (
          <>
            {/* Los protocolos primero: son el modo de trabajo correcto; el registro
                libre es para lo que ningún protocolo cubre todavía. */}
            {protocolos.map(p => (
              <div className="card" key={p.id}>
                <div className="grow">
                  <div className="t">{p.nombre}</div>
                  <div className="s">{p.cuando}</div>
                </div>
                <button className="ib pri" onClick={() => setCorriendo(p)}>Correr el protocolo</button>
              </div>
            ))}
            <div className="card">
              <div className="grow">
                <div className="t">Registrar una intervención libre</div>
                <div className="s">Instalación, mantenimiento, diagnóstico, visita de garantía o
                  reparación cobrada — con fotos y a qué venta es achacable.</div>
              </div>
              <button className="ib" onClick={() => setForma('intervencion')}>Registrar</button>
            </div>
          </>
        )}
        {intervenciones === null && <div className="empty">Cargando…</div>}
        {intervenciones?.length === 0 && (
          <div className="empty">
            <b>La bitácora está vacía.</b>
            <p>Aquí queda todo lo que ocurra en esta casa: cada visita con su técnico,
               sus fotos y su causa — el historial que la memoria no guarda.</p>
          </div>
        )}
        {intervenciones?.map(iv => {
          const g = iv.garantiaId ? garantiaDe(iv.garantiaId) : null
          return (
            <div className="card" key={iv.id}>
              <div className="grow">
                <div className="t" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="grow">{TIPOS_INTERVENCION[iv.tipo] || iv.tipo}</span>
                  {iv.protocoloId && (
                    <button className="ib sm" onClick={() => setDictamen(iv)}>Dictamen</button>
                  )}
                </div>
                <div className="s">{iv.tecnico} · {fechaCorta(iv.fecha)}
                  {nombreCot(iv.cotizacionId) ? ` · ${nombreCot(iv.cotizacionId)}` : ''}
                  {iv.facturaRef ? ` · factura ${iv.facturaRef}` : ''}</div>
                {g && (
                  <div className="s" style={{ marginTop: 4 }}>
                    Amparada por la garantía vigente hasta {g.fin}{g.facturaRef ? ` (factura ${g.facturaRef})` : ''}
                  </div>
                )}
                {iv.protocoloNombre && (
                  <div className="s" style={{ marginTop: 4 }}>
                    <b>Protocolo seguido:</b> {iv.protocoloNombre}
                  </div>
                )}
                <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{iv.descripcion}</div>
                {iv.respuestas?.length > 0 && (
                  <div className="s" style={{ marginTop: 6 }}>
                    {iv.respuestas.map((r, k) => (
                      <div key={k}>
                        • {r.paso}: {r.tipo === 'medicion'
                          ? `${r.valor}${r.unidad ? ` ${r.unidad}` : ''}`
                          : r.ok ? 'cumple' : 'NO cumple'}
                        {r.nota ? ` — ${r.nota}` : ''}
                      </div>
                    ))}
                  </div>
                )}
                <FotosRevision fotos={iv.fotos} />
              </div>
            </div>
          )
        })}
      </main>
    </div>
  )
}
